import { Component, useState, type ReactNode } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { SITE } from "@/lib/config";
import { LenisProvider } from "@/lib/lenis";
import { Navbar, SystemStrip } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { BootSequence } from "@/components/BootSequence";
import Home from "@/pages/Home";
import Protocol from "@/pages/Protocol";
import HowItWorks from "@/pages/HowItWorks";
import Outcomes from "@/pages/Outcomes";
import Reputation from "@/pages/Reputation";
import TerminalPage from "@/pages/TerminalPage";
import Docs from "@/pages/Docs";
import Ecosystem from "@/pages/Ecosystem";
import Token from "@/pages/Token";
import Manifesto from "@/pages/Manifesto";
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-base p-8 text-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.24em] text-error">System fault</p>
            <p className="mt-3 font-mono text-sm text-dim">{String(this.state.error.message)}</p>
            <button
              onClick={() => this.setState({ error: null })}
              className="mt-6 border border-line px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Shell() {
  const location = useLocation();
  const [booting, setBooting] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
    try {
      return localStorage.getItem(SITE.bootStorageKey) !== "1";
    } catch {
      return false;
    }
  });

  return (
    <div className="flex min-h-screen flex-col bg-base">
      <Navbar />
      <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home ready={!booting} />} />
          <Route path="/protocol" element={<Protocol />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/outcomes" element={<Outcomes />} />
          <Route path="/reputation" element={<Reputation />} />
          <Route path="/terminal" element={<TerminalPage />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/ecosystem" element={<Ecosystem />} />
          <Route path="/token" element={<Token />} />
          <Route path="/manifesto" element={<Manifesto />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
      <div className="mt-auto">
        <SystemStrip />
        <Footer />
      </div>
      {booting && <BootSequence onComplete={() => setBooting(false)} />}
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LenisProvider>
          <Shell />
        </LenisProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
